import CloseIcon from "@mui/icons-material/Close";
import { Button, Dialog, Divider, Stack, Typography } from "@mui/material";
import { type FC, useCallback, useState } from "react";

import { ActivityFilter24h } from "~/components/filters/ActivityFilter24h";
import { ApyFilter } from "~/components/filters/ApyFilter";
import { CreatedDateFilter } from "~/components/filters/CreatedDateFilter";
import { EndsInFilter } from "~/components/filters/EndsInFilter";
import { useFiltersScope } from "~/components/filters/FiltersScope";
import {
  LiquidityRangeFilter,
  useLiquidityRangeFilter,
} from "~/components/filters/LiquidityRangeFilter";
import { PlatformFilter } from "~/components/filters/PlatformFilter";
import { ProbabilityFilter } from "~/components/filters/ProbabilityFilter";
import { ResetFiltersButton } from "~/components/filters/ResetFiltersButton";
import { RoiFilter } from "~/components/filters/RoiFilter";
import { SpreadFilter } from "~/components/filters/SpreadFilter";
import {
  useVolumeRangeFilter,
  VolumeRangeFilter,
} from "~/components/filters/VolumeRangeFilter";
import { FilterRangeForm } from "~/components/UI/FilterRange/components/FilterRangeForm";
import { FilterButton } from "~/feature/dashboard/components/sortAndFilters";
import { SwitchGrouping } from "~/feature/event/components/SwitchGrouping";
import { useScreenResolution } from "~/hooks/useScreenResolution";
import type { IRangeValue } from "~/types/range";

import * as Styled from "./MarketsFiltersBar.styles";
import * as DialogStyled from "./MarketsFiltersDialog.styles";

interface IMarketsFiltersContentProps {
  platforms?: string[];
}

const MarketsFiltersContent: FC<IMarketsFiltersContentProps> = ({
  platforms,
}) => (
  <>
    <PlatformFilter platforms={platforms} />
    <ProbabilityFilter />
    <SpreadFilter />
    <LiquidityRangeFilter />
    <VolumeRangeFilter />
    <EndsInFilter />
    <CreatedDateFilter />
    <ApyFilter />
    <RoiFilter />
    <ActivityFilter24h />
  </>
);

interface IMarketsFiltersBarProps {
  showGroupingSwitch?: boolean;
  platforms?: string[];
}

interface IMarketsFiltersDialogProps {
  open: boolean;
  handleClose: () => void;
  showGroupingSwitch?: boolean;
  platforms?: string[];
}

const MarketsFiltersDialog: FC<IMarketsFiltersDialogProps> = ({
  open,
  handleClose,
  showGroupingSwitch = false,
  platforms,
}) => {
  const { clearAllFilters, isFiltersApplied } = useFiltersScope();
  const { onChangeVolumeFilter, initialValue: volumeInitialValue } =
    useVolumeRangeFilter();
  const { onChangeLiquidityFilter, initialValue: liquidityInitialValue } =
    useLiquidityRangeFilter();

  const getVolumeValues = (value: IRangeValue) => {
    onChangeVolumeFilter(value);
  };

  const getLiquidityValues = (value: IRangeValue) => {
    onChangeLiquidityFilter(value);
  };

  return (
    <Dialog open={open} onClose={handleClose} fullScreen>
      <DialogStyled.DialogContent>
        <DialogStyled.DialogHeader>
          <Typography variant="caption" sx={{
            fontWeight: 700
          }}>
            Filters
          </Typography>
          <CloseIcon onClick={handleClose} />
        </DialogStyled.DialogHeader>
        {showGroupingSwitch && (
          <DialogStyled.FilterRow>
            <DialogStyled.Filter>
              <DialogStyled.FilterLabel variant="caption">
                Grouping
              </DialogStyled.FilterLabel>
              <SwitchGrouping />
            </DialogStyled.Filter>
          </DialogStyled.FilterRow>
        )}
        <DialogStyled.FilterRow>
          <DialogStyled.Filter>
            <DialogStyled.FilterLabel variant="caption">
              Platform
            </DialogStyled.FilterLabel>
            <PlatformFilter platforms={platforms} />
          </DialogStyled.Filter>
        </DialogStyled.FilterRow>
        <DialogStyled.FilterRow>
          <DialogStyled.Filter>
            <DialogStyled.FilterLabel variant="caption">
              Probability
            </DialogStyled.FilterLabel>
            <ProbabilityFilter />
          </DialogStyled.Filter>
        </DialogStyled.FilterRow>
        <DialogStyled.FilterRow>
          <DialogStyled.Filter>
            <DialogStyled.FilterLabel variant="caption">
              Spread
            </DialogStyled.FilterLabel>
            <SpreadFilter />
          </DialogStyled.Filter>
        </DialogStyled.FilterRow>
        <DialogStyled.FilterRow>
          <DialogStyled.Filter>
            <DialogStyled.FilterLabel variant="caption">
              Liquidity
            </DialogStyled.FilterLabel>
            <FilterRangeForm
              key={`${open}-${liquidityInitialValue?.min ?? ""}-${liquidityInitialValue?.max ?? ""}`}
              initialValue={liquidityInitialValue}
              getValues={getLiquidityValues}
              isMobile
            />
          </DialogStyled.Filter>
        </DialogStyled.FilterRow>
        <DialogStyled.FilterRow>
          <DialogStyled.Filter>
            <DialogStyled.FilterLabel variant="caption">
              Volume
            </DialogStyled.FilterLabel>
            <FilterRangeForm
              key={`${open}-${volumeInitialValue?.min ?? ""}-${volumeInitialValue?.max ?? ""}`}
              initialValue={volumeInitialValue}
              getValues={getVolumeValues}
              isMobile
            />
          </DialogStyled.Filter>
        </DialogStyled.FilterRow>
        <DialogStyled.FilterRow>
          <DialogStyled.Filter>
            <DialogStyled.FilterLabel variant="caption">
              Ends in
            </DialogStyled.FilterLabel>
            <EndsInFilter />
          </DialogStyled.Filter>
        </DialogStyled.FilterRow>
        <DialogStyled.FilterRow>
          <DialogStyled.Filter>
            <DialogStyled.FilterLabel variant="caption">
              Created date
            </DialogStyled.FilterLabel>
            <CreatedDateFilter />
          </DialogStyled.Filter>
        </DialogStyled.FilterRow>
        <DialogStyled.FilterRow>
          <DialogStyled.Filter>
            <DialogStyled.FilterLabel variant="caption">
              Min APY
            </DialogStyled.FilterLabel>
            <ApyFilter />
          </DialogStyled.Filter>
        </DialogStyled.FilterRow>
        <DialogStyled.FilterRow>
          <DialogStyled.Filter>
            <DialogStyled.FilterLabel variant="caption">
              Min ROI
            </DialogStyled.FilterLabel>
            <RoiFilter />
          </DialogStyled.Filter>
        </DialogStyled.FilterRow>
        <DialogStyled.FilterRow>
          <DialogStyled.Filter>
            <DialogStyled.FilterLabel variant="caption">
              24h Activity
            </DialogStyled.FilterLabel>
            <ActivityFilter24h />
          </DialogStyled.Filter>
        </DialogStyled.FilterRow>
        <Divider />
        <DialogStyled.FilterRow>
          <DialogStyled.Filter>
            <Button
              variant="contained"
              disabled={!isFiltersApplied}
              onClick={handleClose}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: "600",
                  textTransform: "none"
                }}>
                Show results
              </Typography>
            </Button>
            <Button
              variant="text"
              disabled={!isFiltersApplied}
              onClick={clearAllFilters}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: "600",
                  textTransform: "none"
                }}>
                Clear filters
              </Typography>
            </Button>
          </DialogStyled.Filter>
        </DialogStyled.FilterRow>
      </DialogStyled.DialogContent>
    </Dialog>
  );
};

export const MarketsFiltersBar: FC<IMarketsFiltersBarProps> = ({
  showGroupingSwitch = false,
  platforms,
}) => {
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const { isMobile } = useScreenResolution();

  const openFilters = useCallback(() => setIsFiltersOpen((prev) => !prev), []);

  const closeFilters = useCallback(() => {
    setIsFiltersOpen(false);
  }, []);

  if (isMobile) {
    return (
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          gap: 1,
          mt: 1,
          mb: 1
        }}>
        <FilterButton active={isFiltersOpen} onClick={openFilters} />
        <MarketsFiltersDialog
          open={isFiltersOpen}
          handleClose={closeFilters}
          showGroupingSwitch={showGroupingSwitch}
          platforms={platforms}
        />
      </Stack>
    );
  }

  return (
    <Styled.FiltersBarWrapper sx={{ mb: 1, py: 1.5 }}>
      <Styled.StyledFiltersBar>
        <Styled.LeftSection>
          {showGroupingSwitch && <SwitchGrouping />}
          <MarketsFiltersContent platforms={platforms} />
          <ResetFiltersButton />
        </Styled.LeftSection>
      </Styled.StyledFiltersBar>
    </Styled.FiltersBarWrapper>
  );
};
